import { WellsResponse } from 'types/api-responses';
import { EMPTY, FieldGrid, fieldText, type Field } from 'components/well-cards';
import { LinkCell } from 'components/link-cell';
import { formatAsFeet } from './utils';

/**
 * The same ten fields, in the same order, as the Well Workspace's Well Details card
 * (pages/well-workspaces/detail/well-details-card.tsx). The two cards are meant to read
 * identically, so anything added to one belongs in the other.
 *
 * The six lifecycle fields this card used to carry — SRP Well Status and the Permit,
 * Spud, Rig Release, Completion and First Production dates — are not lost: they are the
 * whole of the Well Status card directly below, on both pages.
 *
 * State and County have no home on the well payload yet (36 keys, neither among them),
 * so they render an em-dash until the backend serves them. They are already on the
 * workspace's staged well, which is why the card there fills them.
 * @param {WellsResponse} well The well.
 * @return {Field[]} The fields, in display order.
 */
const fieldsFor = (well: WellsResponse): Field[] => [
  { label: 'UWI', value: fieldText(well.uwi) },
  { label: 'SRP Well Name', value: fieldText(well.name) },
  {
    label: 'Current Operator',
    value: (
      <LinkCell value={well.currentOperator} href={well.currentOperatorHref} />
    ),
  },
  { label: 'Hole Direction', value: fieldText(well.holeDirection) },
  { label: 'State', value: fieldText(well.stateAbbreviation) },
  { label: 'County', value: fieldText(well.county) },
  { label: 'SIM Landing Zone', value: fieldText(well.srpLandingZone) },
  {
    label: 'Vendor Measured Depth',
    value: formatAsFeet(well.measuredDepth) ?? EMPTY,
  },
  {
    label: 'Vendor True Vertical Depth',
    value: formatAsFeet(well.trueVerticalDepth) ?? EMPTY,
  },
  // Last Production Date lives on Well Status with the other lifecycle dates (UAT).
];

export const WellDetailsContent = ({ well }: { well: WellsResponse }) => (
  <FieldGrid fields={fieldsFor(well)} />
);
